
/**
 * Insurance Download Service
 *
 * This service provides functionality to export insurance acquisition dashboard data
 * to an Excel file, including report generation, formatting, and filter documentation.
 * It uses ExcelJS for workbook creation and FileSaver for file download.
 *
 * Main features:
 * - Generates an Excel report with a summary/introduction and compliance data
 * - Applies filters and documents them in the report
 * - Formats and styles the Excel output for clarity
 * - Handles confidential/proprietary information notices
 *
 * @fileoverview Service for exporting insurance compliance data to Excel in Angular.
 * @author Fannie Mae
 */

import { Injectable } from '@angular/core';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { InsrAcqnDashboardDetails } from '../model/InsuranceAcquisitionDetails';

@Injectable({
  providedIn: 'root'
})

/**
 * Service for exporting insurance acquisition dashboard data to Excel.
 */
export class InsrDownloadService {

  private confidential_info = 'Fannie Mae Confidential and Proprietary'; // Constant for confidential information
  private confidential_info_body = '** This data is for informational purposes only. The information represents only the data compiled for those Mortgage Loans and associated properties delivered during the specified date range and the other applied filters. The list may be incomplete, and the data may contain inaccuracies based on incorrect input or incorrect association to the Mortgage Loan. The presence or absence of a Mortgage Loan and associated data on the list does not establish compliance with the requirements and you should check your systems and submissions to ensure that complete data was submitted as and when required.';


  /**
   * Initializes the service.
   */
  constructor() { }


  /**
   * Exports the provided insurance acquisition dashboard data and filters to an Excel file.
   * @param dataRaw Array of insurance acquisition dashboard details to export.
   * @param filters Object containing filter criteria applied to the data.
   */
  public exportToExcel(dataRaw: InsrAcqnDashboardDetails[], filters: any): void {
    const workbook = new ExcelJS.Workbook();
    this.createIntroductionSheet(workbook, filters);
    this.createComplianceReportSheet(workbook, dataRaw);

    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      saveAs(blob, this.generateFileName('Digitizer Insurance Compliance Reporting', 'xlsx'));
    });
  }

  /**
   * Creates the introduction worksheet documenting report title, filters, and notices.
   * @param workbook The ExcelJS workbook instance.
   * @param filters The filters applied to the report data.
   */
  private createIntroductionSheet(workbook: any, filters: any) {
    const worksheetIntroduction = workbook.addWorksheet('Report Inputs');
    worksheetIntroduction.getCell('A1').value = 'DUS Digitizer Insurance Data Submission Compliance Report';
    // Bold the text, increase font size, and set background color for cell A1
    worksheetIntroduction.getCell('A1').font = {
      bold: true,
      size: 16 // Large font size
    };
    worksheetIntroduction.getCell('A1').fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'D9D9D9' } // // Light grey background
    };
    // Set the width of column A
    worksheetIntroduction.getColumn('A').width = 80; // Adjust the width as needed
    // Set the height of row 1
    worksheetIntroduction.getRow(1).height = 30; // Adjust the height as needed
    // Vertically center the content in row 1
    worksheetIntroduction.getCell('A1').alignment = {
      vertical: 'middle'
    };

    // Add filters to the introduction worksheet
    worksheetIntroduction.addRow(['Filtered by:']);
    let dateRange = '  Date Range: ' + filters.startDate + ' - ' + filters.endDate;
    worksheetIntroduction.addRow([dateRange]);
 
    if (filters.selectedLender) {
      worksheetIntroduction.addRow(['  Lender: ' + filters.selectedLender]);
    } else {
      worksheetIntroduction.addRow(['  Lender: All']);
    }

    if (filters.requireFloodINS) {
      worksheetIntroduction.addRow(['  Requires Flood Insurance: ' + filters.requireFloodINS]);
    } else {
      worksheetIntroduction.addRow(['  Requires Flood Insurance: All']);
    }

    if (filters.acquisitionStatus) {
      worksheetIntroduction.addRow(['  Acquisition Status: ' + filters.acquisitionStatus]);
    } else {
      worksheetIntroduction.addRow(['  Acquisition Status: All']);
    }

    if (filters.supplemental) {
      worksheetIntroduction.addRow(['  Supplemental: ' + filters.supplemental]);
    } else {
      worksheetIntroduction.addRow(['  Supplemental: All']);
    }

    if (filters.loadFinanceType) {
      worksheetIntroduction.addRow(['  Loan Refinance Type: ' + filters.loadFinanceType]);
    } else {
      worksheetIntroduction.addRow(['  Loan Refinance Type: All']);
    }

    if (filters.smallLoanBalance) {
      worksheetIntroduction.addRow(['  Small Loan Balance: ' + filters.smallLoanBalance]);
    } else {
      worksheetIntroduction.addRow(['  Small Loan Balance: All']);
    }

    if (filters.transactionType) {
      worksheetIntroduction.addRow(['  Transaction Type: ' + filters.transactionType]);
    } else {
      worksheetIntroduction.addRow(['  Transaction Type: All']);
    }

    if (filters.tableSearchText) {
      worksheetIntroduction.addRow(['  Search Text: ' + filters.tableSearchText]);
    } else {
      worksheetIntroduction.addRow(['  Search Text: All']);
    }

    worksheetIntroduction.addRow([' ']); // Add an empty row for spacing
    worksheetIntroduction.addRow([' ']); // Add an empty row for spacing
    let row = worksheetIntroduction.addRow([this.confidential_info]);
    row.font = { bold: true };

    row = worksheetIntroduction.addRow([this.confidential_info_body]);
    row.getCell(1).alignment = { wrapText: true };
  }

  /**
   * Creates the main compliance report worksheet with formatted data and notices.
   * @param workbook The ExcelJS workbook instance.
   * @param dataRaw The insurance acquisition dashboard data to include in the report.
   */
  private createComplianceReportSheet(workbook: any, dataRaw: InsrAcqnDashboardDetails[]) {
    const worksheetComplianceReport = workbook.addWorksheet('Digitizer Insurance Compliance Report');

    // Define headers for the Excel sheet
    const headers = [
      'Seller Name',
      'Collateral Reference Number',
      'DUS Gateway Deal ID',
      'Property Name',
      'Loan Acquisition Date',
      'Submission Deadline',
      'Flood Insurance (Flood INS) Report Submission Date',
      'Days Flood INS Received Prior To / (After) Submission Deadline',
      'Submitted via API',
      'ACORD 28 (A28) Report Submission Date',
      'Days A28 Received Prior To / (After) Submission Deadline',
      // 'A28 Submitted via API',
      'ALTA Survey (ALTA) Report Submission Date',
      'Days ALTA Received Prior To / (After) Submission Deadline',
      // 'ALTA Submitted via API',
      'Flood Analysis Worksheet (FAW) Report Submission Date',
      'Days FAW Received Prior To / (After) Submission Deadline',
      // 'FAW Submitted via API',
      'NFIP Policy (NFIP) Report Submission Date',
      'Days NFIP Received Prior To / (After) Submission Deadline',
      // 'NFIP Submitted via API',
      'Private or Excess Flood Coverage Policy (PFX) Report Submission Date',
      'Days PFX Received Prior To / (After) Submission Deadline',
      // 'PFX Submitted via API',
      'Standard Flood Hazard Determination Form (SFHDF) Report Submission Date',
      'Days SFHDF Received Prior To / (After) Submission Deadline',
      // 'SFHDF Submitted via API',
      'Schedule of Values (SOV) Report Submission Date',
      'Days SOV Received Prior To / (After) Submission Deadline',
      // 'SOV Submitted via API',
      'ACORD 25 (A25) Report Submission Date',
      'Days A25 Received Prior To / (After) Submission Deadline',
      // 'A25 Submitted via API',
      'Property Policy (PROP) Report Submission Date',
      'Days PROP Received Prior To / (After) Submission Deadline',
      // 'PROP Submitted via API',
      'Liability Policy (LIA) Report Submission Date',
      'Days LIA Received Prior To / (After) Submission Deadline'
      // 'LIA Submitted via API'
    ];

    // Prepare data rows from the input data
    const data: string[][] = [];
    dataRaw.forEach((item, row) => {
      row += 1; // Increment row counter
      // console.log(
      //   'row = ' + row,
      //   "aqsnDt = " + this.convertDateToMMDDYYYY(row, item.aqsnDt, '') +
      //   'reqdSubmtDt = ' + this.convertDateToMMDDYYYY(row, item.reqdSubmtDt, '')
      // );

      // Push formatted data into the data array that will be added to the Excel sheet, including date conversions and API submission check
      data.push([item.lender,
      item.coltRefNo,
      item.dusDealId,
      item.propNme,
      this.convertDateToMMDDYYYY(row, item.aqsnDt, ''),  // Loan Acquisition Date
      this.convertDateToMMDDYYYY(row, item.reqdSubmtDt, 'N/A'),  // Submission Deadline
      
      this.convertDateSubmissionDate(row, item.propInsrSubmtDt, item.reqdSubmtDt, item.insr, item.acquisitionStatus),  // Flood Insurance (Flood INS) Report Submission Date 
      this.calculateDaysBetweenDates(row, item.propInsrSubmtDt, item.reqdSubmtDt),  // Days Flood INS Received Prior To / (After) Submission Deadline
      this.isApiSubmission(item.filenameINSR),
      
      this.convertDateSubmissionDateAllPerils(row, item.propAccord28SmtdDttm, item.reqdSubmtDt, item.propAccord28Typ, item.acquisitionStatus), // ACORD 28 (A28) Report Submission Date  
      this.calculateDaysBetweenDates(row, item.propAccord28SmtdDttm, item.reqdSubmtDt), // Days A28 Received Prior To / (After) Submission Deadline
      // A28 Submitted via API  

      this.convertDateSubmissionDateAllPerils(row, item.propAmrcnLandTtlAsscSmtdDttm, item.reqdSubmtDt, item.propAmrcnLandTtlAsscTyp, item.acquisitionStatus), // ALTA Survey (ALTA) Report Submission Date
      this.calculateDaysBetweenDates(row, item.propAmrcnLandTtlAsscSmtdDttm, item.reqdSubmtDt), // Days ALTA Received Prior To / (After) Submission Deadline
      // ALTA Submitted via API

      this.convertDateSubmissionDateAllPerils(row, item.propFldAnlysWkshSmtdDttm, item.reqdSubmtDt, item.propFldAnlysWkshTyp, item.acquisitionStatus), // Flood Analysis Worksheet (FAW) Report Submission Date
      this.calculateDaysBetweenDates(row, item.propFldAnlysWkshSmtdDttm, item.reqdSubmtDt), // Days FAW Received Prior To / (After) Submission Deadline  
      // FAW Submitted via API

      this.convertDateSubmissionDate(row, item.propNfipSmtdDttm, item.reqdSubmtDt, item.propNfipTyp, item.acquisitionStatus), // NFIP Policy (NFIP) Report Submission Date
      this.calculateDaysBetweenDates(row, item.propNfipSmtdDttm, item.reqdSubmtDt), // Days NFIP Received Prior To / (After) Submission Deadline
      // NFIP Submitted via API

      this.convertDateSubmissionDateAllPerils(row, item.propPrvtOrExcsFldInsrCvrgSmtdDttm, item.reqdSubmtDt, item.propPrvtOrExcsFldInsrCvrgTyp, item.acquisitionStatus), // Private or Excess Flood Coverage Policy (PFX) Report Submission Date
      this.calculateDaysBetweenDates(row, item.propPrvtOrExcsFldInsrCvrgSmtdDttm, item.reqdSubmtDt), // Days PFX Received Prior To / (After) Submission Deadline
      // PFX Submitted via API

      this.convertDateSubmissionDateAllPerils(row, item.propSpclFldHzrdDtmnFormSmtdDttm, item.reqdSubmtDt, item.propSpclFldHzrdDtmnFormTyp, item.acquisitionStatus), // Special Flood Hazard Determination Form (SFHDF) Report Submission Date
      this.calculateDaysBetweenDates(row, item.propSpclFldHzrdDtmnFormSmtdDttm, item.reqdSubmtDt), // Days SFHDF Received Prior To / (After) Submission Deadline
      // SFHDF Submitted via API

      this.convertDateSubmissionDateAllPerils(row, item.propSchOfValSmtdDttm, item.reqdSubmtDt, item.propSchOfValTyp, item.acquisitionStatus), // Schedule of Values (SOV) Report Submission Date
      this.calculateDaysBetweenDates(row, item.propSchOfValSmtdDttm, item.reqdSubmtDt), // Days SOV Received Prior To / (After) Submission Deadline
      // SOV Submitted via API

      this.convertDateSubmissionDateAllPerils(row, item.propAccord25SmtdDttm, item.reqdSubmtDt, item.propAccord25Typ, item.acquisitionStatus), // ACORD 25 (A25) Report Submission Date
      this.calculateDaysBetweenDates(row, item.propAccord25SmtdDttm, item.reqdSubmtDt), // Days A25 Received Prior To / (After) Submission Deadline
      // A25 Submitted via API

      this.convertDateSubmissionDateAllPerils(row, item.propInsrSmtdDttm, item.reqdSubmtDt, item.propInsrTyp, item.acquisitionStatus), // Property Policy (PROP) Report Submission Date
      this.calculateDaysBetweenDates(row, item.propInsrSmtdDttm, item.reqdSubmtDt), // Days PROP Received Prior To / (After) Submission Deadline
      // PROP Submitted via API

      this.convertDateSubmissionDateAllPerils(row, item.propLiabPlcySmtdDttm, item.reqdSubmtDt, item.propLiabPlcyTyp, item.acquisitionStatus), // Liability Policy (LIA) Report Submission Date
      this.calculateDaysBetweenDates(row, item.propLiabPlcySmtdDttm, item.reqdSubmtDt), // Days LIA Received Prior To / (After) Submission Deadline
      // LIA Submitted via API
      ]);
    });

    // Add headers to the worksheet
    worksheetComplianceReport.addRow(headers);

    // Style the header row
    const headerRow = worksheetComplianceReport.getRow(1);
    headerRow.eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'D9D9D9' } }; // Light grey background
      cell.font = { bold: false, color: { argb: '000000' } }; // Black text
      cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true }; // Center alignment
      cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } }; // Thin border
    });

    // Add data rows to the worksheet
    data.forEach((row, rowIndex) => {
      const excelRow = worksheetComplianceReport.addRow(row);

      // Apply red font to specific cells with brackets
      row.forEach((cellValue, colIndex) => {
        if (colIndex > 6 && typeof cellValue === 'string' && /\(\d+\)/.test(cellValue)) {
          const cell = excelRow.getCell(colIndex + 1);
          cell.font = { color: { argb: 'FFFF0000' } }; // Red color
        }
      });
    });

    // Freeze the top row
    worksheetComplianceReport.views = [{ state: 'frozen', ySplit: 1 }];

    // Apply alternating column shading and alignment
    worksheetComplianceReport.eachRow((row, rowNumber) => {
      if (rowNumber > 1) { // Skip the header row
        row.eachCell((cell, colNumber) => {
          if (colNumber > 7 && colNumber % 2 === 0) {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F2F2F2' } }; // Light gray background
          }
          if (colNumber === 2 || colNumber >= 5) {
            cell.alignment = { horizontal: 'right' }; // Right-align specific columns
          }
          if (colNumber === 9) {
            cell.alignment = { horizontal: 'center' }; // center alignment for Submitted via API
          }

          cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } }; // Thin border
        });
      }
    });

    // Set fixed column widths
    const columnWidth = 15;
    const columnWidthLarge = 55;
    worksheetComplianceReport.columns = [
      { width: columnWidthLarge }, // Seller Name
      { width: columnWidth + 1 }, // Collateral Reference #
      { width: columnWidth }, // DUS Gateway Deal ID
      { width: columnWidthLarge }, // Property Name
      { width: columnWidth }, // Loan Acquisition Date
      { width: columnWidth }, // Submission Deadline
      { width: columnWidth }, // Flood Insurance (Flood INS) Report Submission Date
      { width: columnWidth }, // Days Flood INS Received Prior To / (After) Submission Deadline
      { width: columnWidth },  // Submitted via API
      { width: columnWidth }, // ACORD 28 (A28) Report Submission Date  
      { width: columnWidth }, // Days A28 Received Prior To / (After) Submission Deadline
      { width: columnWidth }, // A28 Submitted via API  

      { width: columnWidth }, // ALTA Survey (ALTA) Report Submission Date
      { width: columnWidth }, // Days ALTA Received Prior To / (After) Submission Deadline  
      // ALTA Submitted via API

      // Flood Analysis Worksheet (FAW) Report Submission Date
      { width: columnWidth }, // Days FAW Received Prior To / (After) Submission Deadline  
      // FAW Submitted via API

      { width: columnWidth }, // NFIP Policy (NFIP) Report Submission Date
      { width: columnWidth }, // Days NFIP Received Prior To / (After) Submission Deadline
      // NFIP Submitted via API

      { width: columnWidth }, // Private or Excess Flood Coverage Policy (PFX) Report Submission Date
      { width: columnWidth }, // Days PFX Received Prior To / (After) Submission Deadline
      // PFX Submitted via API

      { width: columnWidth }, // Special Flood Hazard Determination Form (SFHDF) Report Submission Date
      { width: columnWidth }, // Days SFHDF Received Prior To / (After) Submission Deadline
      // SFHDF Submitted via API

      { width: columnWidth }, // Schedule of Values (SOV) Report Submission Date
      { width: columnWidth }, // Days SOV Received Prior To / (After) Submission Deadline
      // SOV Submitted via API

      { width: columnWidth }, // ACORD 25 (A25) Report Submission Date
      { width: columnWidth }, // Days A25 Received Prior To / (After) Submission Deadline
      // A25 Submitted via API

      { width: columnWidth }, // Property Policy (PROP) Report Submission Date
      { width: columnWidth }, // Days PROP Received Prior To / (After) Submission Deadline
      // PROP Submitted via API

      { width: columnWidth }, // Liability Policy (LIA) Report Submission Date
      { width: columnWidth } // Days LIA Received Prior To / (After) Submission Deadline
      // LIA Submitted via API
    ];

    worksheetComplianceReport.addRow([' ']); // Add an empty row for spacing
    worksheetComplianceReport.addRow([' ']); // Add an empty row for spacing

    worksheetComplianceReport.addRow([' ']); // Add an empty row for spacing
    worksheetComplianceReport.addRow([' ']); // Add an empty row for spacing

    let row = worksheetComplianceReport.addRow([this.confidential_info]);
    row.font = { bold: true };
    // Merge the next two cells in the same row
    let rowIndex = row.number; // Get the current row index
    worksheetComplianceReport.mergeCells(rowIndex, 1, rowIndex, 4); // Merge cells B and C in the current row
    // Left-justify the content in the merged cell
    row.getCell(1).alignment = { horizontal: 'left', vertical: 'top' };

    row = worksheetComplianceReport.addRow([this.confidential_info_body]);
    rowIndex = row.number; // Get the current row index
    worksheetComplianceReport.mergeCells(rowIndex, 1, rowIndex, 4); // Merge cells B and C in the current row
    row.getCell(1).alignment = { horizontal: 'left', vertical: 'top', wrapText: true };
    // Set row height manually (adjust based on content length)
    row.height = 110; // Adjust the height value as needed    
  }

  /**
   * Determines if the submission was made via API based on the filename.
   * @param filenameINSR The filename to check.
   * @returns 'Yes' if API submission, 'No' otherwise.
   */
  private isApiSubmission(filenameINSR: string) {
    if (filenameINSR && filenameINSR.length > 0) {
        return filenameINSR.includes('_INS_API_JSON_Submission.json') ? 'Yes' : 'No';
      }
    return "";
  }

  /**
   * Converts a date to MM/DD/YYYY format.
   * @param row The row index (for error logging).
   * @param date The date to convert.
   * @param submission Submission status string (for special handling).
   * @returns The formatted date string or 'N/A' if not available.
   */
  private convertDateToMMDDYYYY(row, date: Date | string, submission: String): string {
    if (!date) {
      if (submission === 'N/A') {
        return 'N/A'; // Return 'N/A' if submission is 'N/A'
      }

      return ''; // Return empty string if date is null or undefined
    }

    try {
      const parsedDate = typeof date === 'string' ? new Date(date) : date; // Parse if input is a string
      const month = String(parsedDate.getMonth() + 1).padStart(2, '0'); // Get month (0-based, so add 1)
      const day = String(parsedDate.getDate()).padStart(2, '0'); // Get day
      const year = parsedDate.getFullYear(); // Get year
      return `${month}/${day}/${year}`; // Format as mm/dd/yyyy
    } catch (error) {
      console.error('Error in convertDateToMMDDYYYY: ', error);
      console.error('Error in row: ', row);
      return '';
    }
  }

  /**
   * Converts the property insurance submission date to MM/DD/YYYY or 'N/A' based on flags.
   * @param row The row index (for error logging).
   * @param propInsrSubmtDt The property insurance submission date.
   * @param reqdSubmtDt The required submission date.
   * @param floodInsrReqdFlag Whether flood insurance is required.
   * @param acquisitionStatus The acquisition status.
   * @returns The formatted date string or 'N/A'.
   */
  private convertDateSubmissionDate(row, propInsrSubmtDt, reqdSubmtDt: Date, insr: string, acquisitionStatus: string): string {
    // If "floodInsrReqdFlag": false and "acquisitionStatus": "Acquired" return N/A
    if ((insr === 'F' || insr === 'N/A') && acquisitionStatus === 'Acquired' && !propInsrSubmtDt) {
      return 'N/A';
    }      

    // If propInsrSubmtDt is null or undefined, check reqdSubmtDt to determine if we return 'N/A' or an empty string  
    if (!propInsrSubmtDt) return !reqdSubmtDt ? 'N/A' : '';

    try {
      const parsedDate = typeof propInsrSubmtDt === 'string' ? new Date(propInsrSubmtDt) : propInsrSubmtDt; // Parse if input is a string
      const month = String(parsedDate.getMonth() + 1).padStart(2, '0'); // Get month (0-based, so add 1)
      const day = String(parsedDate.getDate()).padStart(2, '0'); // Get day
      const year = parsedDate.getFullYear(); // Get year
      return `${month}/${day}/${year}`; // Format as mm/dd/yyyy
    } catch (error) {
      console.error('Error in convertDateToMMDDYYYY: ', error);
      console.error('Error in row: ', row);
      return '';
    }
  }

  /**
   * Calculates the number of days between two dates, returning negative values in brackets.
   * @param row 
   * @param submtDt 
   * @param reqdSubmtDt 
   * @param insr 
   * @param acquisitionStatus 
   * @returns 
   */
  private convertDateSubmissionDateAllPerils(row: number, submtDt, reqdSubmtDt: Date, allPerilsType: string, acquisitionStatus: string): string {
    if (allPerilsType === 'N/A') {
      return 'N/A';
    }      

    // If propInsrSubmtDt is null or undefined, check reqdSubmtDt to determine if we return 'N/A' or an empty string  
    if (!submtDt) return !reqdSubmtDt ? 'N/A' : '';

    try {
      const parsedDate = typeof submtDt === 'string' ? new Date(submtDt) : submtDt; // Parse if input is a string
      const month = String(parsedDate.getMonth() + 1).padStart(2, '0'); // Get month (0-based, so add 1)
      const day = String(parsedDate.getDate()).padStart(2, '0'); // Get day
      const year = parsedDate.getFullYear(); // Get year
      return `${month}/${day}/${year}`; // Format as mm/dd/yyyy
    } catch (error) {
      console.error('Error in convertDateToMMDDYYYY: ', error);
      console.error('Error in row: ', row);
      return '';
    }
  }

  /**
   * Calculates the number of days between two dates, returning negative values in brackets.
   * @param row The row index (for error logging).
   * @param propInsrSubmtDt The property insurance submission date.
   * @param reqdSubmtDt The required submission date.
   * @returns The number of days as a string, negative values in brackets, or 'N/A'.
   */
  private calculateDaysBetweenDates(row, propInsrSubmtDt, reqdSubmtDt: Date | string | null): string {
    if (!reqdSubmtDt) {
      return 'N/A'; // Return 'N/A' if submission is 'N/A'
    }

    propInsrSubmtDt = this.convertDateToMMDDYYYY(row, propInsrSubmtDt, '');
    reqdSubmtDt = this.convertDateToMMDDYYYY(row, reqdSubmtDt, '');

    if (!propInsrSubmtDt || !reqdSubmtDt) {
      return ''; // Return empty string if either date is null or empty
    }

    try {
      // Parse the input strings as dates
      const start = new Date(propInsrSubmtDt);
      const end = new Date(reqdSubmtDt);

      // Validate the parsed dates
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        return ''; // Return empty string if either date is invalid
      }

      // Calculate the difference in milliseconds
      const differenceInTime = end.getTime() - start.getTime();

      // Convert the difference to days
      let differenceInDays;
      if (differenceInTime >= 0) {
        differenceInDays = Math.ceil(differenceInTime / (1000 * 60 * 60 * 24));
      } else {
        differenceInDays = Math.floor(differenceInTime / (1000 * 60 * 60 * 24));
      }

      // Return the result, with negative values in brackets
      return differenceInDays < 0 ? `(${Math.abs(differenceInDays)})` : differenceInDays.toString();
    } catch (error) {
      console.error('Error calculating days between dates:', error);
      return ''; // Return empty string in case of an error
    }
  }

  /**
   * Generates a file name with a timestamp for the exported Excel file.
   * @param baseName The base name for the file.
   * @param extension The file extension (e.g., 'xlsx').
   * @returns The generated file name string.
   */
  private generateFileName(baseName: string, extension: string): string {
    const now = new Date();
  
    // Format date and time
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0'); // Months are 0-based
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
  
    // Combine into a timestamp
    const timestamp = `${year}-${month}-${day}_${hours}-${minutes}-${seconds}`;
  
    // Return the file name
    return `${baseName}_${timestamp}.${extension}`;
  }
}